import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  PURCHASE_METHOD, PURCHASE_METHOD_OPTIONS, PURCHASE_METHOD_PENDING, PurchaseMethodGateStatus
} from '@shared-logic/purchase-method';
import { PURCHASE_METHOD_RESPONSE } from '@shared-logic/integration-ibm';
import { fieldType } from 'src/app/shared/const/options';
import { appState } from 'src/app/shared/interfaces/appState.interface';
import { FilterByObjectPipe } from 'src/app/shared/pipes/filter/filter-by-object';
import { Field } from '../fields.component';

@Component({
  selector: 'app-purchase-method',
  templateUrl: './purchase-method.component.html',
  styleUrls: ['./../fields.component.scss'],
})
export class PurchaseMethodComponent extends Field implements OnInit, OnChanges {
  @Input() valueToSet: number = 0;
  @Input() isRequired: boolean = true;
  @Input() buyerCode: number = 0;
  /* Single state instead of four independent booleans (ADR-0001/ADR-0002, R4): the
  template switches on this one value, so at most one leyenda can ever render. null
  means nothing to report -- idle, or a value not yet checked. */
  @Input() purchaseMethodStatus: PurchaseMethodGateStatus | null = null;
  @Input() purchaseMethodMessageError: string | null = null;
  /* Phase flag, not an error flag: only read while purchaseMethodStatus is pending, to
  pick the right progress caption. */
  @Input() isAutocompletingPurchaseMethod: boolean = false;
  @Input() isEditable: boolean = true;
  /* Drives the Fasón explanatory note only -- kept separate from isEditable because
  add-buyer overloads that input with "also not mid-validation", which would make the
  note flash on for any non-Fasón order while its validation is in flight. */
  @Input() isFasonContract: boolean = false;
  @Output() purchaseMethodEvent = new EventEmitter<number>();
  /* R9: the unavailable state's only way out short of reloading the screen. Each
  surface decides what to retry (default lookup vs. validation) because only it knows
  whether the field currently has a value. */
  @Output() retryPurchaseMethod = new EventEmitter<void>();

  public purchaseMethods = PURCHASE_METHOD;
  public purchaseMethodOptions: string[] = PURCHASE_METHOD_OPTIONS;
  public readonly PURCHASE_METHOD_STATUS = PURCHASE_METHOD_RESPONSE;
  public readonly PURCHASE_METHOD_PENDING = PURCHASE_METHOD_PENDING;

  constructor(store: Store<appState>) {
    super(store);
    this.pipe = new FilterByObjectPipe();
  }

  ngOnInit(): void {
    this.tag = 'purchaseMethod';
    this.OBJECT = this.purchaseMethods;
    this.array = this.purchaseMethodOptions;
    this.setIdentifyClassName();

    setTimeout(() => {
      this.rememberValueInForm();
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['valueToSet'] && this.fieldIdentificator == fieldType.ADD_BUYER) {
      this.rememberValueInForm();
    }
  }

  override rememberValueInForm() {
    if (this.fieldIdentificator == fieldType.ADD_BUYER) {
      this.elementSelected = this.valueToSet > 0 ? this.purchaseMethods[this.valueToSet] : '';
      this.setIsOptionSelected(this.valueToSet > 0);
      return;
    }

    this.rememberValueWithObject();
  }

  setValueWithEvent(event: { element: unknown; index: number }) {
    if (!this.isEditable) {
      return;
    }
    const valueFromElement = Number(event?.element);
    if (!Number.isNaN(valueFromElement) && valueFromElement > 0) {
      this.setElement(valueFromElement, event.index);
      return;
    }

    const optionByIndex = this.purchaseMethodOptions[event?.index ?? -1];
    const valueFromIndex = Number(optionByIndex);
    if (!Number.isNaN(valueFromIndex) && valueFromIndex > 0) {
      this.setElement(valueFromIndex, event.index);
    }
  }

  override setElement(element: any, index: number) {
    const valueToSave = Number(element);

    if (this.fieldIdentificator != fieldType.ADD_BUYER) {
      this.elementSelected = valueToSave > 0 ? this.purchaseMethods[valueToSave] : '';
      this.search = this.elementSelected;
      this.placeHolder = '';
      this.setElementToGenerate(valueToSave);
      this.changeOptionSelected();
      this.changeShowOptions();
      return;
    }

    this.valueToSet = valueToSave;
    this.elementSelected = valueToSave > 0 ? this.purchaseMethods[valueToSave] : '';
    this.search = this.elementSelected;
    this.placeHolder = '';
    this.purchaseMethodEvent.emit(valueToSave);
    this.setIsOptionSelected(true);
    this.setShowOptions(false);
  }
}
